import { useState, useEffect, useMemo, useRef } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { pollApi } from "../../utils/apiPolling";
import { X_API_KEY } from "../../constants";
import { API_URLS } from "../../config/apiRoutes";
import { URL_CONFIG } from "../../config/urlConfig";
import { generateRandomString } from "../../utils/utilFunctions";

export const TERMS_AND_CONDITIONS = {
  title: "Welcome to Across Assist Scanner",
  intro:
    "Welcome to the Across Assist Scanner. Please read these Terms and Conditions carefully before using the scanner service.",
  items: [
    {
      title: "Acceptance of Terms",
      description:
        "By accessing or using the Across Assist Scanner, you agree to be bound by these Terms. If you disagree with any part of the terms, then you may not access the service.",
    },
    {
      title: "Use of Service",
      description:
        "The Across Assist scanner is provided for the purpose of secure policy submission. You agree to use the service only for lawful purposes and in accordance with these Terms.",
    },
    {
      title: "Privacy",
      description:
        "Your privacy is important to us. All information submitted through the Across Assist Scanner will be handled in accordance with our Privacy Policy. Please review our Privacy Policy for more information.",
    },
    {
      title: "User Responsibilities",
      description:
        "You are responsible for maintaining the confidentiality of your information and for all activities that occur under your account. You agree to notify us immediately of any unauthorized use of your account.",
    },
    {
      title: "Intellectual Property",
      description:
        "The Across Assist Scanner and its original content, features, and functionality are and will remain the exclusive property of Across Assist and its licensors.",
    },
    {
      title: "Limitation of Liability",
      description:
        "In no event shall Across Assist, nor its directors, employees, partners, agents, suppliers, or affiliates, be liable for any indirect, incidental, special, consequential or punitive damages, including without limitation, loss of profits, data, use, goodwill, or other intangible losses, resulting from (i) your use or inability to use the service; (ii) any unauthorized access to or use of our servers and/or any personal information stored therein.",
    },
    {
      title: "Changes to Terms",
      description:
        "We reserve the right, at our sole discretion, to modify or replace these Terms at any time. If a revision is material, we will provide at least 30 days' notice prior to any new terms taking effect.",
    },
    {
      title: "Governing Law",
      description:
        "These Terms shall be governed and construed in accordance with the laws of the jurisdiction in which Across Assist operates, without regard to its conflict of law provisions.",
    },
    {
      title: "Contact Us",
      description:
        "If you have any questions about these Terms, please contact us at apb@acrossassist.com or 0120-4501441",
    },
  ],
  conclusion:
    'By clicking "I agree to the Terms & Conditions", you acknowledge that you have read, understood, and agree to be bound by these Terms and Conditions.',
};

// Reactified: useScannerData now takes baseApbUrl as an argument (from parent), and returns Styles as well.

export const useScannerData = () => {
  const [openModal, setOpenModal] = useState(true);
  const [tncChecked, setTncChecked] = useState(false);
  const [bbpsId, setBbpsId] = useState("");

  const baseApbUrl = `${URL_CONFIG.ROUTE_URL}/camera-recorder`;

  const randomStringParam = useRef(generateRandomString(6));

  const navigate = useNavigate();

  // Extract bbpsId from URL params dynamically
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const id = params.get("bbps") || params.get("bbpsid") || "";

    setBbpsId(id);
    if (id) {
      localStorage.setItem("bbpsid", id);
    }
  }, []);

  // Lock scroll when modal is open
  useEffect(() => {
    document.body.style.overflow = openModal ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [openModal]);

  // Dynamically generate QR code URL
  const qrCodeUrl = useMemo(() => {
    if (!baseApbUrl) return "";
    if (bbpsId) {
      const hasQuery = baseApbUrl.includes("?");
      return `${baseApbUrl}${hasQuery ? "&" : "?"}bbpsid=${encodeURIComponent(
        bbpsId
      )}-${randomStringParam.current}`;
    }
    return baseApbUrl;
  }, [baseApbUrl, bbpsId]);

  // Dynamic button styles based on T&C checked
  const buttonStyles = {
    backgroundColor: tncChecked ? "#003366!important" : "#90caf9!important",
    color: "#fff",
    fontWeight: 700,
    fontFamily: "Poppins, 'serif'",
    boxShadow: "0 2px 8px 0 rgba(0,0,0,0.03)",
    borderRadius: "8px",
    padding: "8px 24px",
    fontSize: "1rem",
    border: `1px solid ${tncChecked ? "#003366" : "#90caf9"}`,
    opacity: tncChecked ? 1 : 0.7,
    transition: "all 0.2s",
    "&:hover": {
      backgroundColor: tncChecked ? "#002244" : "#90caf9",
      boxShadow: tncChecked ? "0 4px 16px 0 rgba(0,0,0,0.08)" : "none",
    },
  };

  const Styles = {
    BlurBg: {
      filter: "blur(8px)",
      WebkitFilter: "blur(8px)",
      transition: "filter 0.3s, -webkit-filter 0.3s",
      background: "#fff",
    },
    ParentContainer: {
      display: "flex",
      textAlign: "center",
      alignItems: "center",
      flexDirection: "column",
      justifyContent: "start",
      mt: 15,
      minHeight: "100vh",
      borderRadius: 4,
      background: "#fff",
    },
    BannerLogo: { height: 50, mb: 3 },
    HeadingTxt: {
      fontWeight: "700",
      fontFamily: "Poppins, 'serif'",
      fontSize: { xs: "1.1rem", md: "1.2rem" },
      color: "#000",
      mb: 2,
    },
    DescriptionTxt: {
      mb: 4,
      fontWeight: "500",
      fontSize: { xs: "0.9rem", md: "1.1rem" },
      color: "#000",
      fontFamily: "Poppins, 'serif'",
    },
    QRCodeContainer: { p: 3, borderRadius: 2, background: "#fff" },
    QRCodeScannerImg: { height: "auto", maxWidth: "100%", width: "100%" },
  };

  // Prevent closing modal by clicking outside or pressing Escape
  const handleDialogClose = (event, reason) => {
    if (reason === "backdropClick" || reason === "escapeKeyDown") {
      // Do nothing, prevent closing
      return;
    }
    setOpenModal(false);
  };

  const checkQrCodeScanned = () => {
    return axios(API_URLS.COMPARE_BBPS_ID, {
      method: "GET",
      params: { secondarybbpsid: `${bbpsId}-${randomStringParam.current}` },
      headers: {
        "x-api-key": X_API_KEY,
        "Content-Type": "application/json",
      },
    });
  };

  const onQrScannedSuccess = () => {
    navigate("/screen-damage-test");
  };

  const onQrScannedEerror = () => {
    navigate("/apb-scanner-error");
  };

  // Handler for the "Continue to Proceed" button
  const handleProceed = () => {
    setOpenModal(false);
    const intervals = [
      5000, 3000, 3000, 3000, 3000, 3000, 3000, 3000, 3000, 3000,
    ];

    pollApi(
      checkQrCodeScanned,
      onQrScannedSuccess,
      onQrScannedEerror,
      intervals
    );
  };

  return {
    openModal,
    tncChecked,
    setTncChecked,
    bbpsId,
    setBbpsId,
    qrCodeUrl,
    buttonStyles,
    handleDialogClose,
    Styles,
    handleProceed,
  };
};
